import type { SponsorAd } from '@/domain/sponsors'
const REQUEST_TIMEOUT_MS = 5_000

interface SharedRequest {
  controller: AbortController
  promise: Promise<SponsorAd[]>
  consumers: number
}
let pendingRequest: SharedRequest | undefined
let cachedList: SponsorAd[] | undefined
function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}
function imageUrl(value: unknown): string | undefined {
  const url = text(value)
  if (!url) return undefined
  if (/^\/(?![\/\\])/.test(url) && !url.includes('\\')) return url
  try { if (['https:', 'http:'].includes(new URL(url).protocol)) return url } catch { /* Invalid image address. */ }
  return undefined
}
function parseList(value: unknown): SponsorAd[] {
  if (!value || typeof value !== 'object' || !('items' in value) || !Array.isArray(value.items)) {
    throw new Error('Invalid sponsor response')
  }
  const seen = new Set<string>()
  return value.items.flatMap((item: unknown) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return []
    const record = item as Record<string, unknown>
    const id = text(record.id), image = imageUrl(record.imageUrl), placement = text(record.placement)
    if (!id || !image || !placement || seen.has(id) || typeof record.expiresAt !== 'string') return []
    seen.add(id)
    const ad: SponsorAd = { id, imageUrl: image, placement, expiresAt: record.expiresAt,
      enabled: record.enabled === undefined || record.enabled === true,
      sortOrder: typeof record.sortOrder === 'number' && Number.isFinite(record.sortOrder) ? record.sortOrder : 0,
      weight: typeof record.weight === 'number' && Number.isFinite(record.weight) && record.weight > 0 ? record.weight : 1,
    }
    for (const key of ['brand', 'title', 'description', 'imageAlt', 'linkLabel'] as const) {
      const entry = text(record[key]); if (entry) ad[key] = entry
    }
    const logo = imageUrl(record.logoUrl); if (logo) ad.logoUrl = logo
    const href = text(record.href)
    if (href) {
      try { if (['https:', 'http:'].includes(new URL(href).protocol)) ad.href = href } catch { /* Invalid link. */ }
    }
    return [ad]
  })
}

async function fetchSponsors(controller: AbortController): Promise<SponsorAd[]> {
  const timer = setTimeout(() => controller.abort(new DOMException('Sponsor request timed out', 'TimeoutError')), REQUEST_TIMEOUT_MS)
  try {
    const headers = new Headers({ accept: 'application/json' })
    const key = import.meta.env.VITE_RANK_API_KEY?.trim()
    if (key) headers.set('authorization', `Bearer ${key}`)
    const response = await fetch('/api/v1/ads', { headers, signal: controller.signal, cache: 'no-store' })
    controller.signal.throwIfAborted()
    if (!response.ok) throw new Error('Sponsors unavailable')
    const value: unknown = await response.json()
    controller.signal.throwIfAborted()
    return parseList(value)
  } finally {
    clearTimeout(timer)
  }
}

/** All placements share one list per page; cancelling one consumer keeps the others alive. */
export async function loadSponsors(options: { signal?: AbortSignal } = {}): Promise<SponsorAd[]> {
  options.signal?.throwIfAborted()
  if (cachedList) return cachedList.slice()
  let request = pendingRequest
  if (!request || request.controller.signal.aborted) {
    const controller = new AbortController()
    const current: SharedRequest = { controller, consumers: 0, promise: Promise.resolve([]) }
    current.promise = fetchSponsors(controller).then(ads => {
      controller.signal.throwIfAborted()
      cachedList = ads
      return ads
    }).finally(() => {
      if (pendingRequest === current) pendingRequest = undefined
    })
    pendingRequest = request = current
  }
  const current = request
  current.consumers++
  return new Promise((resolve, reject) => {
    let settled = false
    function finish(complete: () => void) {
      if (settled) return
      settled = true
      options.signal?.removeEventListener('abort', abort)
      current.consumers--
      if (!current.consumers && pendingRequest === current) current.controller.abort()
      complete()
    }
    function abort() { finish(() => reject(options.signal!.reason)) }
    options.signal?.addEventListener('abort', abort, { once: true })
    current.promise.then(ads => finish(() => resolve(ads.slice())), error => finish(() => reject(error)))
  })
}
